import React from 'react';
import { Plus, X } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';

/**
 * The name/phone contact list, as it appears on the Distributor and Outlet forms.
 *
 * Shared so that capturing a party during a visit offers exactly the same thing
 * as capturing it from its own screen - that parity was the whole point of the
 * on-the-fly forms, and a second hand-written copy is how they drift apart.
 *
 * The first row is always the primary contact.
 */
export const emptyContact = (isPrimary = false) => ({ name: '', phone: '', is_primary: isPrimary });

export const ContactsField = ({
  contacts,
  onChange,
  label = 'Contacts',
  testIdPrefix = 'contact',
}) => {
  const rows = contacts.length ? contacts : [emptyContact(true)];

  const update = (index, field, value) => {
    const next = rows.map((c, i) => (i === index ? { ...c, [field]: value } : c));
    onChange(next);
  };

  const add = () => onChange([...rows, emptyContact(false)]);

  const remove = (index) => {
    const next = rows.filter((_, i) => i !== index);
    // Whoever is first is the primary contact.
    onChange(next.map((c, i) => ({ ...c, is_primary: i === 0 })));
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <Label>{label}</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={add}
          className="rounded-xl"
          data-testid={`${testIdPrefix}-add`}
        >
          <Plus className="w-4 h-4 mr-1" /> Add Contact
        </Button>
      </div>

      {rows.map((contact, index) => (
        <div key={index} className="flex gap-2 items-center">
          <Input
            placeholder="Name"
            value={contact.name}
            onChange={(e) => update(index, 'name', e.target.value)}
            className="flex-1 h-10 rounded-xl"
            data-testid={`${testIdPrefix}-name-${index}`}
          />
          <Input
            placeholder="Phone"
            value={contact.phone}
            onChange={(e) => update(index, 'phone', e.target.value)}
            className="flex-1 h-10 rounded-xl"
            data-testid={`${testIdPrefix}-phone-${index}`}
          />
          {rows.length > 1 && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => remove(index)}
              data-testid={`${testIdPrefix}-remove-${index}`}
            >
              <X className="w-4 h-4 text-red-500" />
            </Button>
          )}
        </div>
      ))}
    </div>
  );
};

export default ContactsField;
